import { create } from "zustand";

/** Chromium's install prompt, held back until the player asks for it. Not in lib.dom yet. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * What we can offer this player about installing:
 *  - installed:  already running as an app, offer nothing
 *  - prompt:     the browser handed us an install prompt (Chrome, Edge, Samsung Internet, Android, desktop)
 *  - ios:        Safari / iOS browsers: no prompt exists, so we show the two taps
 *  - ios-inapp:  inside another app's browser (Facebook, Instagram…), which can't add to the home screen at all
 *  - android:    Android without a prompt to hand us (already offered, dismissed, or another browser): point at the menu
 *  - none:       nothing to offer (Firefox, desktop Safari, no service-worker support…)
 */
export type InstallKind = "installed" | "prompt" | "ios" | "ios-inapp" | "android" | "none";

export interface InstallEnv {
  standalone: boolean;
  canPrompt: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}

export const isIos = (e: Pick<InstallEnv, "userAgent" | "platform" | "maxTouchPoints">): boolean =>
  /iPhone|iPad|iPod/.test(e.userAgent) || (e.platform === "MacIntel" && e.maxTouchPoints > 1); // iPadOS says it is a Mac

/** Other apps' built-in browsers. Their share sheet has no "Add to Home Screen". */
export const isInAppBrowser = (userAgent: string): boolean => /FBAN|FBAV|FB_IAB|Instagram|Snapchat|Line\/|MicroMessenger|Twitter|LinkedInApp|Pinterest|GSA\//.test(userAgent);

export const isAndroid = (userAgent: string): boolean => /Android/.test(userAgent);

/**
 * Whether to greet this player with the one-time "Pass the Verse is an app now" pop-up. Only people who already play (a
 * brand-new player has the welcome screen to read, and the install card finds them after a solve), only where installing
 * is possible, never once installed, and never twice.
 */
export function shouldShowInstallIntro(o: { alreadySeen: boolean; hasHistory: boolean; kind: InstallKind; userAgent: string }): boolean {
  if (o.alreadySeen || !o.hasHistory || o.kind === "installed") return false;
  return o.kind === "prompt" || o.kind === "ios" || o.kind === "ios-inapp" || o.kind === "android";
}

export function installKind(e: InstallEnv): InstallKind {
  if (e.standalone) return "installed";
  if (e.canPrompt) return "prompt";
  if (isIos(e)) return isInAppBrowser(e.userAgent) ? "ios-inapp" : "ios";
  if (isAndroid(e.userAgent)) return "android";
  return "none";
}

interface PwaState {
  /** The saved install prompt, if the browser offered one. */
  installEvent: BeforeInstallPromptEvent | null;
  standalone: boolean;
  /** A newer version has been downloaded and is waiting for the player to reload into it. */
  updateReady: boolean;
}

export const usePwa = create<PwaState>()(() => ({ installEvent: null, standalone: false, updateReady: false }));

const standaloneNow = (): boolean =>
  window.matchMedia("(display-mode: standalone)").matches || window.matchMedia("(display-mode: minimal-ui)").matches || window.matchMedia("(display-mode: fullscreen)").matches || (navigator as { standalone?: boolean }).standalone === true;

/** What to offer right now. Reads the browser each call, so it is always current. */
export function currentInstallKind(s: Pick<PwaState, "installEvent" | "standalone">): InstallKind {
  return installKind({ standalone: s.standalone, canPrompt: s.installEvent !== null, userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints });
}

/** Ask the browser to install. Resolves to whether the player said yes. */
export async function promptInstall(): Promise<boolean> {
  const ev = usePwa.getState().installEvent;
  if (!ev) return false;
  // A saved prompt can be used once, win or lose.
  usePwa.setState({ installEvent: null });
  try {
    await ev.prompt();
    return (await ev.userChoice).outcome === "accepted";
  } catch {
    return false;
  }
}

let registration: ServiceWorkerRegistration | undefined;
let reloading = false;

/** Switch to the waiting version. The page reloads itself once the new worker has taken over. */
export function applyUpdate(): void {
  const waiting = registration?.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }
  reloading = true;
  waiting.postMessage({ type: "SKIP_WAITING" });
}

const RECHECK_EVERY_MS = 30 * 60_000;

/**
 * Called once at startup. Listeners go on first (Chrome fires `beforeinstallprompt` early, possibly before the app has
 * rendered); then the service worker is registered, in production builds only so dev never serves stale files.
 */
export function initPwa(): void {
  usePwa.setState({ standalone: standaloneNow() });
  window.matchMedia("(display-mode: standalone)").addEventListener("change", () => usePwa.setState({ standalone: standaloneNow() }));
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // we show our own button, at a moment the player chose
    usePwa.setState({ installEvent: e as BeforeInstallPromptEvent });
  });
  window.addEventListener("appinstalled", () => usePwa.setState({ installEvent: null, standalone: true }));

  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;

  const sw = navigator.serviceWorker;
  sw.addEventListener("controllerchange", () => {
    // Only reload when the player asked for the update. The first install also fires this (the worker claims the open
    // page), and that must not yank a game out from under anyone.
    if (reloading) window.location.reload();
  });

  const watch = (reg: ServiceWorkerRegistration) => {
    // `controller` is null on the very first visit: nothing to update from, so no prompt.
    const ready = () => sw.controller && usePwa.setState({ updateReady: true });
    if (reg.waiting) ready();
    reg.addEventListener("updatefound", () => {
      const next = reg.installing;
      next?.addEventListener("statechange", () => next.state === "installed" && ready());
    });
  };

  window.addEventListener("load", () => {
    sw.register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((reg) => {
        registration = reg;
        watch(reg);
        // An installed app can stay open for days, so look for a new version when it comes back to the foreground.
        let last = Date.now();
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState !== "visible" || Date.now() - last < RECHECK_EVERY_MS) return;
          last = Date.now();
          reg.update().catch(() => undefined);
        });
      })
      .catch((err: unknown) => console.warn("Service worker not registered:", err));
  });
}
